/**
 * Loan Tape Extraction Mapping — rendered after the answer markdown as two
 * Export-style buttons ("Map" and "SQL"). Clicking a button opens a large
 * modal: "Map" shows the schema-graph provenance diagram at full size, "SQL"
 * shows the frozen spine SQL. Neither is rendered inline in the answer.
 */

import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Code2, Workflow, X } from "lucide-react";
import SchemaGraphBlockView from "./SchemaGraphBlockView";
import SqlHighlight from "./SqlHighlight";
import type { SchemaGraphEdge, SchemaGraphNode } from "./Chat/Chat.types";

interface SchemaMappingBlockViewProps {
	title?: string;
	nodes: SchemaGraphNode[];
	edges: SchemaGraphEdge[];
	sql?: string;
}

type Modal = "map" | "sql" | null;

const SchemaMappingBlockView: React.FC<SchemaMappingBlockViewProps> = ({
	title,
	nodes,
	edges,
	sql,
}) => {
	const hasMap = nodes.length > 0;
	const hasSql = Boolean(sql && sql.trim());
	const [modal, setModal] = useState<Modal>(null);

	const heading = title ?? "Loan Tape Extraction Mapping";
	const modalTitle = `${heading} — ${modal === "sql" ? "SQL" : "Map"}`;

	const button =
		"inline-flex items-center gap-2 h-8 px-3 rounded-md bg-[#004C4B] " +
		"text-white text-sm font-medium hover:bg-[#008782] transition-colors";

	return (
		<div className="my-3">
			<div className="flex flex-wrap items-center gap-2">
				<button type="button" className={button} onClick={() => setModal("map")}>
					<Workflow className="w-4 h-4" />
					Map
				</button>
				<button type="button" className={button} onClick={() => setModal("sql")}>
					<Code2 className="w-4 h-4" />
					SQL
				</button>
			</div>

			{modal &&
				createPortal(
					<div
						className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 p-4"
						onClick={() => setModal(null)}
					>
					<div
						className="w-full max-w-5xl max-h-[88vh] flex flex-col bg-white rounded-2xl shadow-xl"
						onClick={(e) => e.stopPropagation()}
					>
						<div className="flex items-center justify-between gap-2 px-5 py-3 border-b border-gray-200 flex-shrink-0">
							<h2 className="text-base font-semibold text-text-strong">
								{modalTitle}
							</h2>
							<button
								type="button"
								onClick={() => setModal(null)}
								className="text-gray-400 hover:text-gray-600"
								aria-label="Close"
							>
								<X className="w-5 h-5" />
							</button>
						</div>

						<div className="flex-1 min-h-0 overflow-auto p-5">
							{modal === "map" ? (
								hasMap ? (
									<SchemaGraphBlockView
										nodes={nodes}
										edges={edges}
										bare
										height="66vh"
									/>
								) : (
									<div className="text-sm text-gray-500">
										No mapping graph is available for this result.
									</div>
								)
							) : null}
							{modal === "sql" ? (
								hasSql ? (
									<SqlHighlight sql={sql ?? ""} />
								) : (
									<div className="text-sm text-gray-500">
										SQL is not available for this result.
									</div>
								)
							) : null}
						</div>
					</div>
				</div>,
				document.body,
			)}
		</div>
	);
};

export default SchemaMappingBlockView;
